import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FirebaseError } from "firebase/app";
import { useAuth } from "../hooks/useAuth";
import { AppFooter } from "../components/AppFooter";

function friendlyError(err: unknown): string {
  if (err instanceof FirebaseError) {
    switch (err.code) {
      case "auth/email-already-in-use":
        return "Für diese E-Mail existiert bereits ein Konto. Einfach anmelden statt registrieren.";
      case "auth/weak-password":
        return "Das Passwort ist zu schwach (mindestens 6 Zeichen).";
      case "auth/invalid-email":
        return "Diese E-Mail-Adresse ist ungültig.";
      default:
        return "Registrierung fehlgeschlagen. Bitte erneut versuchen.";
    }
  }
  return "Registrierung fehlgeschlagen. Bitte erneut versuchen.";
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError("Die Passwörter stimmen nicht überein.");
      return;
    }
    setSubmitting(true);
    try {
      await register(email, password);
      navigate("/clubs");
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-dvh bg-chalk-50">
      <div className="hero-fairway flex flex-col items-center justify-end px-6 pb-14 pt-20 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/25 backdrop-blur-sm">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-8 w-8 text-chalk-50">
            <path d="M6 21V4l12 5.5L6 15" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h1 className="mt-5 font-display text-3xl font-semibold tracking-tight text-chalk-50">Konto erstellen</h1>
        <p className="mt-1.5 text-sm text-turf-100/80">Für Zugriff auf ClubConnect</p>
      </div>

      <div className="mx-auto -mt-8 max-w-md px-6 pb-12">
        <form onSubmit={handleSubmit} className="card animate-fade-up space-y-4 rounded-3xl p-6" noValidate>
          <div>
            <label htmlFor="reg-email" className="mb-1.5 block text-sm font-medium text-ink-900">
              E-Mail
            </label>
            <input
              id="reg-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 w-full rounded-xl border border-chalk-200 bg-chalk-50 px-4 text-base outline-none transition-colors focus:border-turf-600 focus:bg-white"
            />
          </div>
          <div>
            <label htmlFor="reg-password" className="mb-1.5 block text-sm font-medium text-ink-900">
              Passwort
            </label>
            <input
              id="reg-password"
              type="password"
              required
              minLength={6}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 w-full rounded-xl border border-chalk-200 bg-chalk-50 px-4 text-base outline-none transition-colors focus:border-turf-600 focus:bg-white"
            />
            <p className="mt-1 text-xs text-ink-400">Mindestens 6 Zeichen.</p>
          </div>
          <div>
            <label htmlFor="reg-confirm" className="mb-1.5 block text-sm font-medium text-ink-900">
              Passwort wiederholen
            </label>
            <input
              id="reg-confirm"
              type="password"
              required
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-12 w-full rounded-xl border border-chalk-200 bg-chalk-50 px-4 text-base outline-none transition-colors focus:border-turf-600 focus:bg-white"
            />
          </div>

          {error && (
            <p role="alert" className="rounded-lg bg-flag-100 px-3 py-2 text-sm text-flag-600">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="h-12 w-full rounded-xl bg-turf-700 text-base font-semibold text-chalk-50 shadow-sm transition-all hover:bg-turf-800 active:scale-[0.985] disabled:opacity-60"
          >
            {submitting ? "Wird erstellt…" : "Konto erstellen"}
          </button>
        </form>

        <Link to="/login" className="mt-6 block text-center text-sm text-ink-400 underline-offset-2 hover:underline">
          Schon ein Konto? Anmelden
        </Link>
        <AppFooter />
      </div>
    </main>
  );
}
